import * as m from "$lib/paraglide/messages";
import { getLocale } from "$lib/paraglide/runtime";

/** BCP 47 tag for Czech/English product copy (cs-CZ dates: `20. 8. 2026`). */
export function intlLocale(): string {
  const loc = getLocale();
  if (loc === "cs") return "cs-CZ";
  if (loc === "en") return "en-GB";
  return loc;
}

function parseDate(value: string | Date): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const text = value.trim();
  if (!text) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    const [y, mo, d] = text.split("-").map(Number);
    const local = new Date(y, mo - 1, d);
    return Number.isNaN(local.getTime()) ? null : local;
  }
  const d = new Date(text);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatNumber(
  value: number | string | null | undefined,
  options?: Intl.NumberFormatOptions,
): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat(intlLocale(), {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
    ...options,
  }).format(n);
}

export function formatMoney(
  value: number | string | null | undefined,
  currency = "CZK",
  options?: Intl.NumberFormatOptions,
): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return "—";
  try {
    // Whole amounts without decimals; fractional ones keep both digits (14,90 €), never rounded.
    const fraction = Number.isInteger(n)
      ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
      : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
    return new Intl.NumberFormat(intlLocale(), {
      style: "currency",
      currency,
      ...fraction,
      ...options,
    }).format(n);
  } catch {
    return `${formatNumber(n)} ${currency}`;
  }
}

const PRICE_UNIT_SUFFIX: Record<string, () => string> = {
  MONTH: m.price_unit_suffix_month,
  M2: m.price_unit_suffix_m2,
  M2_MONTH: m.price_unit_suffix_m2_month,
  M2_YEAR: m.price_unit_suffix_m2_year,
  YEAR: m.price_unit_suffix_year,
  DAY: m.price_unit_suffix_day,
  HOUR: m.price_unit_suffix_hour,
  M2_DAY: m.price_unit_suffix_m2_day,
  M2_HOUR: m.price_unit_suffix_m2_hour,
};

/** Compact unit suffix for a listing price ("/měs.", "/m²/měs."); rentals default to month. */
export function priceUnitSuffix(
  unit: string | null | undefined,
  category?: string | null,
): string {
  const effective = unit ?? (category === "RENT" || category === "SUBLET" ? "MONTH" : null);
  if (!effective || effective === "PROPERTY") return "";
  return PRICE_UNIT_SUFFIX[effective]?.() ?? "";
}

/**
 * Listing price with unit: "35 000 Kč/měs.", "14,90 €/m²/měs.", "30 000 000 Kč".
 * Empty amount → "Cena na dotaz" when flagged on request, else "—".
 */
export function formatPropertyPrice(
  amount: number | string | null | undefined,
  currency: string | null | undefined,
  unit: string | null | undefined,
  category?: string | null,
  opts?: { onRequest?: boolean | null },
): string {
  if (amount === null || amount === undefined || amount === "") {
    return opts?.onRequest ? m.public_site_price_on_request() : "—";
  }
  const money = formatMoney(amount, currency || "CZK");
  if (money === "—") return money;
  return `${money}${priceUnitSuffix(unit, category)}`;
}

/**
 * Short money for constrained UI (KPIs, board cards, column headers).
 * Uses locale compact notation (`130,9 mil. Kč` / `£130.9M`) above `threshold`
 * (default 1e6); smaller values stay full. Pair with `title={formatMoney(...)}`.
 */
export function formatMoneyCompact(
  value: number | string | null | undefined,
  currency = "CZK",
  options?: Intl.NumberFormatOptions & { threshold?: number },
): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return "—";
  const { threshold = 1_000_000, ...intlOptions } = options ?? {};
  if (Math.abs(n) < threshold) return formatMoney(n, currency, intlOptions);
  try {
    return new Intl.NumberFormat(intlLocale(), {
      style: "currency",
      currency,
      notation: "compact",
      compactDisplay: "short",
      maximumFractionDigits: 1,
      ...intlOptions,
    }).format(n);
  } catch {
    return formatNumberCompact(n, intlOptions);
  }
}

/** Compact plain numbers for dense KPIs (`12.5K`). Same threshold idea as money. */
export function formatNumberCompact(
  value: number | string | null | undefined,
  options?: Intl.NumberFormatOptions & { threshold?: number },
): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return "—";
  const { threshold = 10_000, ...intlOptions } = options ?? {};
  if (Math.abs(n) < threshold) return formatNumber(n, intlOptions);
  return new Intl.NumberFormat(intlLocale(), {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 1,
    ...intlOptions,
  }).format(n);
}

export function formatPercent(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat(intlLocale(), {
    style: "percent",
    maximumFractionDigits: digits,
  }).format(value);
}

/** Area with unit — `25 m²`, never `25.00`. */
export function formatArea(value: number | string | null | undefined, unit = "m²"): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${formatNumber(n, { maximumFractionDigits: 1, minimumFractionDigits: 0 })} ${unit}`;
}

/** Local calendar date as `YYYY-MM-DD` for `<input type="date">`. */
export function toDateInputValue(value: string | Date | null | undefined): string {
  if (!value) return "";
  const d = value instanceof Date ? value : parseDate(value);
  if (!d) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Start of local day from `YYYY-MM-DD` as ISO UTC. */
export function dateInputToStartIso(value: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/** End of local day from `YYYY-MM-DD` as ISO UTC. */
export function dateInputToEndIso(value: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const d = new Date(`${value}T23:59:59.999`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const d = parseDate(value);
  if (!d) return "—";
  return new Intl.DateTimeFormat(intlLocale(), {
    day: "numeric",
    month: "numeric",
    year: "numeric",
  }).format(d);
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const d = parseDate(value);
  if (!d) return "—";
  return new Intl.DateTimeFormat(intlLocale(), {
    day: "numeric",
    month: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

export function formatTime(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const d = parseDate(value);
  if (!d) return "—";
  return new Intl.DateTimeFormat(intlLocale(), { timeStyle: "short" }).format(d);
}

/** Compact date + time for agenda carry-over rows. */
export function formatShortDateTime(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const d = parseDate(value);
  if (!d) return "—";
  return new Intl.DateTimeFormat(intlLocale(), {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

export function displayName(first?: string | null, last?: string | null, fallback = "—"): string {
  const name = [first, last].filter(Boolean).join(" ").trim();
  return name || fallback;
}

/** Start/end of local calendar month as ISO strings for DateTime filters. */
export function monthRangeIso(anchor = new Date()): { start: string; end: string } {
  const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1, 0, 0, 0, 0);
  const end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0, 23, 59, 59, 999);
  return { start: start.toISOString(), end: end.toISOString() };
}

export function startOfDayIso(d = new Date()): string {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.toISOString();
}

export function endOfDayIso(d = new Date()): string {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x.toISOString();
}

function enumMessageKey(name: string, catalog?: string): string[] {
  const snake = name.trim().toUpperCase().replace(/-/g, "_");
  const lower = snake.toLowerCase();
  const keys: string[] = [];
  if (catalog) keys.push(`enum_${catalog}_${lower}`);
  keys.push(`enum_${lower}`);
  return keys;
}

function lookupMessage(key: string): string | null {
  const fn = (m as Record<string, unknown>)[key];
  if (typeof fn !== "function") return null;
  const result = (fn as () => unknown)();
  return typeof result === "string" && result ? result : null;
}

/** Resolve a Paraglide key, or return fallback when the key is missing. */
export function localizeMessage(key: string, fallback: string): string {
  return lookupMessage(key) ?? fallback;
}

function messageKeySlug(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/** Localized automation template fields — never fall back to BE English copy. */
export function localizeAutomationTemplate(template: {
  slug: string;
  name: string;
  description?: string | null;
  category?: string | null;
}): { name: string; description: string; category: string } {
  const slug = messageKeySlug(template.slug);
  const categoryRaw = template.category?.trim() || "";
  const categorySlug = messageKeySlug(categoryRaw);
  const name =
    lookupMessage(`automation_template_${slug}_name`) ??
    `${lookupMessage("automation_template_fallback_name") ?? "Template"} (${template.slug})`;
  const description =
    lookupMessage(`automation_template_${slug}_description`) ??
    lookupMessage("automation_template_fallback_description") ??
    "";
  return {
    name,
    description,
    category: categorySlug
      ? localizeMessage(`automation_template_category_${categorySlug}`, categoryRaw || slug)
      : categoryRaw || localizeMessage("automation_template_category_general", "general"),
  };
}

/**
 * Localized enum label from `messages/{locale}.json` (`enum_one_kk` → `1+kk`).
 * Never Title-Cases GraphQL names (`One Kk`).
 */
export function formatEnum(
  value: string | number | null | undefined,
  catalog?: string,
): string {
  if (value == null || value === "") return "—";
  const text = typeof value === "string" ? value.trim() : String(value);
  if (!text) return "—";
  for (const key of enumMessageKey(text, catalog)) {
    const label = lookupMessage(key);
    if (label) return label;
  }
  if (!text.includes("_") && /^\d+$/.test(text)) return text;
  return text.replace(/_/g, " ");
}

/** Prefer Paraglide `enum_*` for activity type; fall back to server title. */
export function formatActivityTitle(
  type: string | null | undefined,
  fallbackTitle?: string | null,
): string {
  if (type) {
    for (const key of enumMessageKey(type)) {
      const label = lookupMessage(key);
      if (label) return label;
    }
  }
  const title = fallbackTitle?.trim();
  if (title) return title;
  return formatEnum(type);
}

/**
 * Audit summary from known `actionKey` messages (`audit_key_property_update`).
 * Unknown keys use a generic localized summary — never raw EN server text in CS UI.
 */
export function formatAuditSummary(opts: {
  actionKey?: string | null;
  summary?: string | null;
}): string {
  const actionKey = opts.actionKey?.trim();
  if (actionKey) {
    const msgKey = `audit_key_${actionKey.replace(/\./g, "_").toLowerCase()}`;
    const localized = lookupMessage(msgKey);
    if (localized) return localized;
  }
  const summary = opts.summary?.trim();
  if (!summary) return "—";
  // Heuristic: ASCII sentence without Czech diacritics → treat as BE English.
  if (/^[A-Za-z0-9 ,.'"_():/-]+$/.test(summary) && /[A-Za-z]{3,}/.test(summary)) {
    return lookupMessage("audit_summary_generic") ?? summary;
  }
  return summary;
}

export function formatLegalForm(raw: string | null | undefined): string {
  if (!raw) return "—";
  const text = raw.trim();
  if (!text) return "—";
  const code = text.match(/\d+/)?.[0];
  if (code) {
    const labeled = lookupMessage(`ares_legal_form_${code}`);
    if (labeled) return labeled;
  }
  return text;
}

/** Label parts for a demand search area (point + radius). */
export function requirementAreaLabel(req: {
  locationAddress?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  radiusKm?: number | null;
}): { place: string; km: number } | null {
  if (req.radiusKm == null) return null;
  const address = req.locationAddress?.trim();
  if (address) return { place: address, km: req.radiusKm };
  if (req.latitude != null && req.longitude != null) {
    return { place: `${formatNumber(req.latitude, { maximumFractionDigits: 4 })}, ${formatNumber(req.longitude, { maximumFractionDigits: 4 })}`, km: req.radiusKm };
  }
  return null;
}
